import StoreUpdateEvents from "../constants/events/StoreUpdateEvents";
import BaseStore from "./BaseStore";
import LoadingStates from "../constants/states/LoadingStates";
import ActionResultEvents from "../constants/events/ActionResultEvents";
import UIInteractionEvents from "../constants/events/UIInteractionEvents";
import {Space} from "../client/SpaceAPIClient";
import {Advertisement} from "../client/AdvertisementAPIClient";
import Time from "../utils/Time";
import SavingStates from "../constants/states/SavingStates";


class PrivateSpaceDetailsState {
    constructor(
        spaceId,
        loadingState,
        savingState,
        calendarLoadingState,
        space,
        unavailableDates,
        currentSelectedAdvertisement,
        currentSelectedReservationRange,
        showSelectedRangeIsTooShortAlert,
        showSelectedRangeOverlapsUnavailableRangeAlert
    ) {
        this.spaceId = spaceId;
        this.loadingState = loadingState;
        this.savingState = savingState;
        this.calendarLoadingState = calendarLoadingState;
        this.space = space;
        this.unavailableDates = unavailableDates;
        this.currentSelectedAdvertisement = currentSelectedAdvertisement;
        this.currentSelectedReservationRange = currentSelectedReservationRange;
        this.showSelectedRangeIsTooShortAlert = showSelectedRangeIsTooShortAlert;
        this.showSelectedRangeOverlapsUnavailableRangeAlert = showSelectedRangeOverlapsUnavailableRangeAlert;
    }

    equals(other) {
        return this.space.equals(other.space) &&
            this.loadingState === other.loadingState &&
            this.savingState === other.savingState &&
            this.calendarLoadingState === other.calendarLoadingState &&
            this.loadingState === other.loadingState &&
            this.unavailableDates === other.unavailableDates &&
            this.currentSelectedAdvertisement === other.currentSelectedAdvertisement &&
            this.currentSelectedReservationRange === other.currentSelectedReservationRange &&
            this.showSelectedRangeIsTooShortAlert === other.showSelectedRangeIsTooShortAlert &&
            this.showSelectedRangeOverlapsUnavailableRangeAlert === other.showSelectedRangeOverlapsUnavailableRangeAlert;
    }
}

class ImmutablePrivateSpaceDetailsStateBuilder {

    constructor() {
        this.spaceId = 0;
        this.loadingState = LoadingStates.INITIALIZED;
        this.savingState = SavingStates.NOT_STARTED;
        this.calendarLoadingState = LoadingStates.INITIALIZED;
        this.space = new Space();
        this.unavailableDates = [];
        this.currentSelectedAdvertisement = new Advertisement(-1, -1, "Not Selected", "", -1, "", 0, 0, true, [], false);
        this.currentSelectedReservationRange = [];
        this.showSelectedRangeIsTooShortAlert = false;
        this.showSelectedRangeOverlapsUnavailableRangeAlert = false;
    }

    setFromState(state) {
        return this.setSpaceId(state.spaceId)
            .setSpace(state.space)
            .setLoadingState(state.loadingState)
            .setSavingState(state.savingState)
            .setCalendarLoadingState(state.calendarLoadingState)
            .setUnavailableDates(state.unavailableDates)
            .setCurrentSelectedAdvertisement(state.currentSelectedAdvertisement)
            .setCurrentSelectedReservationRange(state.currentSelectedReservationRange)
            .setShowSelectedRangeIsTooShortAlert(state.showSelectedRangeIsTooShortAlert)
            .setShowSelectedRangeOverlapsUnavailableRangeAlert(state.showSelectedRangeOverlapsUnavailableRangeAlert);
    }

    setSpaceId(spaceId) {
        this.spaceId = spaceId;
        return this;
    }

    setSpace(space) {
        this.space = space;
        return this;
    }

    setLoadingState(loadingState) {
        this.loadingState = loadingState;
        return this;
    }

    setSavingState(savingState) {
        this.savingState = savingState;
        return this;
    }

    setCalendarLoadingState(calendarLoadingState) {
        this.calendarLoadingState = calendarLoadingState;
        return this;
    }

    setUnavailableDates(unavailableDates) {
        this.unavailableDates = unavailableDates;
        return this;
    }

    setCurrentSelectedAdvertisement(currentSelectedAdvertisement) {
        this.currentSelectedAdvertisement = currentSelectedAdvertisement;
        return this;
    }

    setCurrentSelectedReservationRange(currentSelectedReservationRange) {
        this.currentSelectedReservationRange = currentSelectedReservationRange;
        return this;
    }

    setShowSelectedRangeIsTooShortAlert(showSelectedRangeIsTooShortAlert) {
        this.showSelectedRangeIsTooShortAlert = showSelectedRangeIsTooShortAlert;
        return this;
    }

    setShowSelectedRangeOverlapsUnavailableRangeAlert(showSelectedRangeOverlapsUnavailableRangeAlert) {
        this.showSelectedRangeOverlapsUnavailableRangeAlert = showSelectedRangeOverlapsUnavailableRangeAlert;
        return this;
    }

    build() {
        return Object.freeze(
            new PrivateSpaceDetailsState(
                this.spaceId,
                this.loadingState,
                this.savingState,
                this.calendarLoadingState,
                this.space,
                this.unavailableDates,
                this.currentSelectedAdvertisement,
                this.currentSelectedReservationRange,
                this.showSelectedRangeIsTooShortAlert,
                this.showSelectedRangeOverlapsUnavailableRangeAlert
            ));
    }
}


class PublicSpaceStore extends BaseStore {

    constructor() {
        super(StoreUpdateEvents.PRIVATE_SPACE_DETAILS_STORE_UPDATE, ImmutablePrivateSpaceDetailsStateBuilder);
        this.subscribeToEvent(ActionResultEvents.PRIVATE_SPACE_DETAILS_LOADED, this.handlePrivateSpaceDetailsLoaded);
        this.subscribeToEvent(ActionResultEvents.PRIVATE_SPACE_DETAILS_NOT_FOUND, this.handlePrivateSpaceDetailsNotFound);
        this.subscribeToEvent(UIInteractionEvents.PRIVATE_SPACE_DETAILS_SET_SPACE_ID, this.handleSetSpaceId);
        this.subscribeToEvent(ActionResultEvents.UNAVAILABLE_SPACE_CALENDAR_LOADED, this.handleUnavailableDatesLoaded);
        this.subscribeToEvent(UIInteractionEvents.PRIVATE_SPACE_DETAILS_ADVERTISEMENT_SELECT_CHANGED, this.handleAdvertisementSelectChanged);
        this.subscribeToEvent(UIInteractionEvents.PRIVATE_SPACE_DETAILS_CALENDAR_SELECT_RANGE_CHANGED, this.handleCalendarSelectionRangeChanged);
        this.subscribeToEvent(UIInteractionEvents.PRIVATE_SPACE_DETAILS_CLOSE_RANGE_CROSSING_ALERT_CLICKED, this.handleRangeCrossingAlertClosed);
        this.subscribeToEvent(UIInteractionEvents.PRIVATE_SPACE_DETAILS_CLOSE_SELECTED_RANGE_TOO_SHORT_ALERT_CLICKED, this.handleRangeShortAlertClosed);
        this.subscribeToEvent(UIInteractionEvents.PRIVATE_SPACE_DETAILS_CREATE_NEW_ADVERTISEMENT_CLICKED, this.handleReservationCreateStart);
        this.subscribeToEvent(UIInteractionEvents.PRIVATE_SPACE_DETAILS_UNMOUNTED, this.resetStore);
    }

    handleReservationCreateStart = (topic, data) => {
        return new ImmutablePrivateSpaceDetailsStateBuilder()
            .setFromState(this.state)
            .setSavingState(SavingStates.SAVING)
            .build();
    };

    handlePrivateSpaceDetailsNotFound = (topic, data) => {
        return new ImmutablePrivateSpaceDetailsStateBuilder()
            .setLoadingState(LoadingStates.FAILED_TO_LOAD)
            .build();
    };

    handlePrivateSpaceDetailsLoaded = (topic, data) => {
        return new ImmutablePrivateSpaceDetailsStateBuilder()
            .setFromState(this.state)
            .setSpace(data)
            .setLoadingState(LoadingStates.FINISHED_LOADING)
            .build();
    };

    handleUnavailableDatesLoaded = (topic, data) => {
        return new ImmutablePrivateSpaceDetailsStateBuilder()
            .setFromState(this.state)
            .setCalendarLoadingState(LoadingStates.FINISHED_LOADING)
            .setUnavailableDates(data.unavailableDates)
            .build();
    };

    handleSetSpaceId = (topic, data) => {
        return new ImmutablePrivateSpaceDetailsStateBuilder()
            .setSpaceId(data.id)
            .setLoadingState(LoadingStates.LOADING)
            .setCalendarLoadingState(LoadingStates.LOADING)
            .build();
    };

    handleAdvertisementSelectChanged = (topic, data) => {
        return new ImmutablePrivateSpaceDetailsStateBuilder()
            .setFromState(this.state)
            .setCurrentSelectedAdvertisement(data.selectedAdvertisement)
            .build();
    };

    handleCalendarSelectionRangeChanged = (topic, data) => {
        const daysBetween =
            Time.calculateDifferenceInDaysBetweenInclusive(data.currentSelectionStartDate, data.currentSelectionEndDate);
        const rangeTooShort = daysBetween < this.state.space.minimumDays;

        let rangesOverlap = false;
        for (const unavailableDate of this.state.unavailableDates) {
            const unavailableStart = Time.parseDate(unavailableDate.startDate);
            const unavailableEnd = Time.parseDate(unavailableDate.endDate);
            if (Time.rangesOverlap(data.currentSelectionStartDate, data.currentSelectionEndDate, unavailableStart, unavailableEnd)) {
                rangesOverlap = true;
                break;
            }
        }

        if (rangeTooShort || rangesOverlap) {
            return new ImmutablePrivateSpaceDetailsStateBuilder()
                .setFromState(this.state)
                .setShowSelectedRangeIsTooShortAlert(rangeTooShort)
                .setShowSelectedRangeOverlapsUnavailableRangeAlert(rangesOverlap)
                .setCurrentSelectedReservationRange([])
                .build();
        }

        return new ImmutablePrivateSpaceDetailsStateBuilder()
            .setFromState(this.state)
            .setShowSelectedRangeOverlapsUnavailableRangeAlert(false)
            .setShowSelectedRangeIsTooShortAlert(false)
            .setCurrentSelectedReservationRange([data.currentSelectionStartDate, data.currentSelectionEndDate])
            .build();
    };

    handleRangeCrossingAlertClosed = (topic, data) => {
        return new ImmutablePrivateSpaceDetailsStateBuilder()
            .setFromState(this.state)
            .setShowSelectedRangeOverlapsUnavailableRangeAlert(false)
            .build();
    };

    handleRangeShortAlertClosed = (topic, data) => {
        return new ImmutablePrivateSpaceDetailsStateBuilder()
            .setFromState(this.state)
            .setShowSelectedRangeIsTooShortAlert(false)
            .build();
    };

}

export default new PublicSpaceStore();